import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  TrendingUp,
  PieChart,
  BarChart3,
  AlertTriangle,
  Lightbulb,
  BookOpen,
  List,
  Target,
  DollarSign,
  Building2,
  Calculator,
  ShieldCheck,
  XCircle,
} from 'lucide-react';

/* ─── FAQs ───────────────────────────────────────────── */

const faqs = [
  {
    q: '¿Es rentable abrir un restaurante en España en 2026?',
    a: 'Depende del tipo de establecimiento y la gestión. El margen neto medio de un restaurante en España se sitúa entre el 3% y el 9%, según datos de Hostelería de España y la FEHR. Los restaurantes bien gestionados, con control de costes riguroso, pueden alcanzar márgenes del 10-15%. Sin embargo, el 60% de los restaurantes cierran antes de cumplir 3 años, principalmente por falta de control financiero.',
  },
  {
    q: '¿Cuánto gana al mes un restaurante medio en España?',
    a: 'Un restaurante medio con una facturación de 30.000-50.000 €/mes puede obtener un beneficio neto de entre 1.500 € y 4.500 € mensuales (margen del 5-9%). Los restaurantes de alta gama o con gestión muy optimizada pueden superar los 5.000-8.000 €/mes de beneficio. Los fast food y dark kitchens, por su menor estructura de costes, pueden alcanzar márgenes del 10-15%.',
  },
  {
    q: '¿Cuál es el coste más importante de un restaurante?',
    a: 'Los dos grandes costes son la materia prima (food cost, 28-35% de la facturación) y el personal (30-35%). Juntos representan entre el 58% y el 70% de los ingresos. El food cost es el más controlable a corto plazo: con escandallos, control de mermas y negociación con proveedores puedes reducirlo entre un 3% y un 8% sin afectar la calidad.',
  },
  {
    q: '¿Qué margen de beneficio es normal en un restaurante?',
    a: 'El margen neto típico en hostelería española oscila entre el 3% y el 9%. Un margen inferior al 3% indica que el negocio está en zona de riesgo. Entre 3-7% es correcto pero mejorable. Entre 7-12% es un restaurante bien gestionado. Por encima del 12% es excelente, habitual en fast food, dark kitchens o restaurantes con ingeniería de menú avanzada.',
  },
  {
    q: '¿Cuánto hay que invertir para abrir un restaurante?',
    a: 'La inversión inicial media para abrir un restaurante en España oscila entre 80.000 € y 250.000 €, dependiendo del tamaño, la ubicación y el nivel de acabados. Un bar de tapas pequeño puede abrirse con 50.000-80.000 €, mientras que un restaurante de gama media-alta en una capital puede requerir 200.000-400.000 €. El periodo medio de recuperación de la inversión (ROI) es de 3-5 años.',
  },
  {
    q: '¿Cómo puedo saber si mi restaurante está perdiendo dinero?',
    a: 'Las señales de alarma son: food cost por encima del 35%, nóminas que superan el 35% de la facturación, retrasos en pagos a proveedores, necesidad de inyecciones de capital recurrentes, y descenso de ticket medio sin aumento de volumen. Un sistema de control de costes como PLEKO te permite detectar estas señales en tiempo real antes de que sea tarde.',
  },
  {
    q: '¿Qué tipo de restaurante es más rentable?',
    a: 'Por margen neto, los más rentables suelen ser: dark kitchens (12-20% de margen), fast food/comida rápida (10-15%), cafeterías especializadas (8-12%) y bares de tapas con buena rotación (7-10%). El fine dining tiene márgenes más bajos (3-7%) por los altos costes de personal y materia prima premium, aunque el ticket medio compensa en volumen.',
  },
  {
    q: '¿Cada cuánto debo revisar la rentabilidad de mi restaurante?',
    a: 'Como mínimo, mensualmente. Lo ideal es tener un cuadro de mando semanal con los KPIs clave: food cost real vs objetivo, coste de personal, ticket medio, número de cubiertos y margen bruto. Con un software como PLEKO, estos datos se actualizan en tiempo real cada vez que escaneas un albarán o registras una venta.',
  },
];

/* ─── Margin table data ──────────────────────────────── */

const marginTable = [
  { tipo: 'Restaurante casual', margenBruto: '60-68%', margenNeto: '5-9%', ticketMedio: '15-25 €', foodCost: '28-32%' },
  { tipo: 'Fine dining', margenBruto: '65-72%', margenNeto: '3-7%', ticketMedio: '60-150 €', foodCost: '30-38%' },
  { tipo: 'Fast food / comida rápida', margenBruto: '65-75%', margenNeto: '10-15%', ticketMedio: '8-14 €', foodCost: '25-30%' },
  { tipo: 'Cafetería / brunch', margenBruto: '68-75%', margenNeto: '8-12%', ticketMedio: '10-18 €', foodCost: '22-28%' },
  { tipo: 'Bar de tapas', margenBruto: '65-72%', margenNeto: '7-10%', ticketMedio: '12-20 €', foodCost: '25-32%' },
  { tipo: 'Food truck', margenBruto: '60-70%', margenNeto: '10-18%', ticketMedio: '8-15 €', foodCost: '28-35%' },
  { tipo: 'Dark kitchen', margenBruto: '55-65%', margenNeto: '12-20%', ticketMedio: '12-22 €', foodCost: '30-35%' },
  { tipo: 'Comedor colectivo', margenBruto: '55-65%', margenNeto: '5-10%', ticketMedio: '8-14 €', foodCost: '32-40%' },
];

/* ─── Traffic light table data ───────────────────────── */

const trafficLight = [
  { rango: '< 3%', nivel: 'ROJO', color: 'bg-red-100 text-red-800 border-red-200', dot: 'bg-red-500', desc: 'Zona de peligro. El negocio no cubre imprevistos ni genera retorno.' },
  { rango: '3 - 7%', nivel: 'AMARILLO', color: 'bg-amber-50 text-amber-800 border-amber-200', dot: 'bg-amber-500', desc: 'Correcto pero frágil. Cualquier subida de costes compromete el resultado.' },
  { rango: '7 - 12%', nivel: 'VERDE', color: 'bg-green-50 text-green-800 border-green-200', dot: 'bg-green-500', desc: 'Restaurante bien gestionado. Margen sano para reinvertir y crecer.' },
  { rango: '> 12%', nivel: 'EXCELENTE', color: 'bg-blue-50 text-blue-800 border-blue-200', dot: 'bg-blue-500', desc: 'Gestión excepcional. Habitual en modelos ligeros (dark kitchen, fast food).' },
];

/* ─── Schema.org JSON-LD ─────────────────────────────── */

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: faq.a },
  })),
};

const articleJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Rentabilidad de un Restaurante: Cuánto Se Gana y Cómo Mejorar los Márgenes',
  description:
    'Guía completa sobre la rentabilidad de restaurantes en España. Datos reales de márgenes, costes principales, fórmulas de cálculo y 7 estrategias para mejorar el beneficio.',
  author: { '@type': 'Organization', name: 'PLEKO' },
  publisher: {
    '@type': 'Organization',
    name: 'PLEKO',
    logo: { '@type': 'ImageObject', url: 'https://pleko.app/favicon.svg' },
  },
  datePublished: '2026-06-18',
  dateModified: '2026-06-18',
  image: 'https://pleko.app/chef-hero.jpg',
};

const breadcrumbJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'PLEKO', item: 'https://pleko.app/' },
    { '@type': 'ListItem', position: 2, name: 'Margen de beneficio', item: 'https://pleko.app/margen-beneficio-restaurante' },
    {
      '@type': 'ListItem',
      position: 3,
      name: 'Rentabilidad restaurante',
      item: 'https://pleko.app/rentabilidad-restaurante',
    },
  ],
};

/* ─── Table of Contents ──────────────────────────────── */

const toc = [
  { id: 'es-rentable', label: '¿Es rentable abrir un restaurante en España?' },
  { id: 'costes-principales', label: 'Los 4 costes principales de un restaurante' },
  { id: 'margenes-tipo', label: 'Márgenes de beneficio por tipo de establecimiento' },
  { id: 'como-calcular', label: 'Cómo calcular la rentabilidad de tu restaurante' },
  { id: 'food-cost', label: 'El food cost: el coste que más puedes controlar' },
  { id: 'estrategias', label: '7 estrategias para mejorar la rentabilidad' },
  { id: 'semaforo', label: 'El semáforo de rentabilidad' },
  { id: 'caso-practico', label: 'Caso práctico: de 2% a 9% de margen' },
  { id: 'herramientas', label: 'Herramientas para controlar la rentabilidad' },
  { id: 'errores', label: 'Errores que destruyen la rentabilidad' },
  { id: 'faqs', label: 'Preguntas frecuentes' },
];

/* ─── Component ──────────────────────────────────────── */

export default function RentabilidadRestaurantePage() {
  return (
    <>
      <Helmet>
        <title>Rentabilidad de un Restaurante: Cuánto Se Gana y Cómo Mejorar los Márgenes [2026] | PLEKO</title>
        <meta
          name="description"
          content="Descubre si es rentable abrir un restaurante en España. Datos reales de márgenes (3-9%), costes principales, fórmulas de cálculo y 7 estrategias probadas para mejorar el beneficio de tu negocio hostelero."
        />
        <meta
          name="keywords"
          content="rentabilidad restaurante, margen restaurante, beneficio restaurante, es rentable abrir un restaurante, cuánto gana un restaurante, margen beneficio hostelería, rentabilidad hostelería, cuánto se gana con un restaurante, margen neto restaurante"
        />
        <link rel="canonical" href="https://pleko.app/rentabilidad-restaurante" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://pleko.app/rentabilidad-restaurante" />
        <meta
          property="og:title"
          content="Rentabilidad de un Restaurante: Cuánto Se Gana y Cómo Mejorar los Márgenes [2026]"
        />
        <meta
          property="og:description"
          content="Guía completa sobre la rentabilidad de restaurantes en España. Márgenes reales, costes, fórmulas y 7 estrategias para mejorar el beneficio."
        />
        <meta property="og:image" content="https://pleko.app/chef-hero.jpg" />
        <meta property="og:locale" content="es_ES" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Rentabilidad de un Restaurante: Cuánto Se Gana [2026] | PLEKO"
        />
        <meta
          name="twitter:description"
          content="¿Es rentable abrir un restaurante? Datos reales de márgenes, costes y estrategias para mejorar el beneficio."
        />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <div className="bg-white">
        {/* ═══════════ HERO ═══════════ */}
        <section className="bg-gradient-to-b from-blue-50 to-white py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
                <TrendingUp className="w-4 h-4" />
                Guía de rentabilidad para hostelería — 2026
              </span>
              <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-6 tracking-tight">
                Rentabilidad de un <span className="text-blue-500">Restaurante</span>: Cuánto Se Gana y Cómo Mejorar los Márgenes
              </h1>
              <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
                El margen neto medio de un restaurante en España es del <strong>3-9%</strong>. Te explicamos con datos reales cómo calcularlo, qué costes puedes controlar y 7 estrategias probadas para mejorar el beneficio de tu negocio.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="#como-calcular"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600 transition-colors shadow-lg shadow-blue-200"
                >
                  Calcular mi rentabilidad
                  <ArrowRight className="w-4 h-4" />
                </a>
                <Link
                  to="/register"
                  className="inline-flex items-center gap-2 px-6 py-3 border-2 border-slate-200 text-slate-700 font-semibold rounded-xl hover:border-slate-300 transition-colors"
                >
                  <Calculator className="w-4 h-4" />
                  Probar PLEKO gratis
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════ CONTENT ═══════════ */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Table of Contents */}
          <nav className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-12">
            <div className="flex items-center gap-2 mb-4">
              <List className="w-5 h-5 text-blue-500" />
              <h2 className="text-lg font-bold text-slate-900">Contenido de esta guía</h2>
            </div>
            <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              {toc.map((item, i) => (
                <li key={item.id}>
                  <a href={`#${item.id}`} className="text-sm text-blue-600 hover:text-blue-800 hover:underline">
                    {i + 1}. {item.label}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <div className="prose prose-lg max-w-none">

            {/* ═══════════ SECTION 1: ¿Es rentable? ═══════════ */}
            <h2 id="es-rentable" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              ¿Es rentable abrir un restaurante en España?
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La respuesta corta: <strong>puede serlo, pero no es fácil</strong>. Según datos del INE y Hostelería de España (2025), la hostelería representa el 6,2% del PIB español y emplea a 1,7 millones de personas. Sin embargo, las cifras de supervivencia son reveladoras:
            </p>

            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-center">
                <p className="text-3xl font-bold text-red-600">60%</p>
                <p className="text-sm text-slate-600 mt-1">de restaurantes cierran antes de 3 años</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-center">
                <p className="text-3xl font-bold text-amber-600">3-9%</p>
                <p className="text-sm text-slate-600 mt-1">margen neto medio del sector</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-5 text-center">
                <p className="text-3xl font-bold text-green-600">315.000</p>
                <p className="text-sm text-slate-600 mt-1">restaurantes activos en España</p>
              </div>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La principal razón de cierre no es la falta de clientes, sino <strong>la falta de control financiero</strong>. Un restaurante que factura 40.000 €/mes pero no controla su food cost, sus mermas ni sus costes de personal puede estar perdiendo dinero sin saberlo. La diferencia entre un restaurante que sobrevive y uno que prospera suele estar en los números, no en la cocina.
            </p>

            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Según el informe anual de la FEHR (Federación Española de Hostelería y Restauración, 2025), los restaurantes que utilizan herramientas de control de costes tienen un margen neto medio <strong>2,5 puntos porcentuales superior</strong> a los que gestionan con métodos manuales o intuición. En un negocio con facturación de 500.000 €/año, esos 2,5 puntos son <strong>12.500 € más de beneficio neto</strong>.
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-10">
              <div className="flex items-start gap-3">
                <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-blue-800">Dato clave</p>
                  <p className="text-blue-700 text-sm mt-1">
                    Un restaurante con 50 cubiertos diarios, ticket medio de 22 € y un margen neto del 7% genera aproximadamente <strong>2.310 €/mes de beneficio</strong>. Subir el margen al 10% (solo 3 puntos) supone <strong>990 € más al mes</strong>, o 11.880 €/año. La rentabilidad se mide en puntos porcentuales, no en sensaciones.
                  </p>
                </div>
              </div>
            </div>

            {/* ═══════════ SECTION 2: Los 4 costes principales ═══════════ */}
            <h2 id="costes-principales" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Los 4 costes principales de un restaurante
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Para entender la rentabilidad, primero hay que entender la estructura de costes. Todo restaurante tiene cuatro grandes partidas que, juntas, representan entre el <strong>80% y el 95%</strong> de la facturación:
            </p>

            <div className="space-y-4 mb-6">
              <div className="border-l-4 border-blue-400 pl-5 py-2">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-blue-500" />
                    1. Materia prima (food cost)
                  </h3>
                  <span className="text-blue-600 font-bold text-lg">28-35%</span>
                </div>
                <p className="text-slate-700 leading-relaxed">
                  El coste de los ingredientes que compras para elaborar los platos. Es el coste <strong>más controlable</strong> a corto plazo. Incluye alimentos, bebidas, productos de limpieza y materias auxiliares. Un restaurante que no hace escandallos no sabe cuánto le cuesta realmente cada plato.
                </p>
              </div>

              <div className="border-l-4 border-green-400 pl-5 py-2">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-green-500" />
                    2. Personal
                  </h3>
                  <span className="text-green-600 font-bold text-lg">30-35%</span>
                </div>
                <p className="text-slate-700 leading-relaxed">
                  Salarios brutos, seguridad social, extras, formación y costes indirectos del equipo. Es el coste más alto y el más difícil de reducir sin afectar al servicio. La eficiencia operativa (turnos bien planificados, polivalencia del personal) es clave para optimizarlo.
                </p>
              </div>

              <div className="border-l-4 border-amber-400 pl-5 py-2">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-amber-500" />
                    3. Alquiler y gastos del local
                  </h3>
                  <span className="text-amber-600 font-bold text-lg">8-12%</span>
                </div>
                <p className="text-slate-700 leading-relaxed">
                  Alquiler, IBI, seguros, mantenimiento, comunidad y amortización de reformas. Es un coste fijo que no varía con el volumen de ventas. La regla del sector dice que el alquiler no debe superar el 10% de la facturación prevista. Si lo supera, el local es demasiado caro para tu modelo de negocio.
                </p>
              </div>

              <div className="border-l-4 border-purple-400 pl-5 py-2">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-purple-500" />
                    4. Otros gastos operativos
                  </h3>
                  <span className="text-purple-600 font-bold text-lg">15-20%</span>
                </div>
                <p className="text-slate-700 leading-relaxed">
                  Suministros (luz, agua, gas), marketing, software, asesoría fiscal, licencias, equipamiento, reparaciones y gastos financieros. Muchos hosteleros subestiman esta partida. Solo la energía puede representar un 4-6% de la facturación tras las subidas de 2022-2025.
                </p>
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-10">
              <p className="font-semibold text-slate-800 mb-3">Distribución típica de costes en un restaurante casual</p>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {[
                  { label: 'Food cost', pct: '30%', color: 'text-blue-600' },
                  { label: 'Personal', pct: '33%', color: 'text-green-600' },
                  { label: 'Alquiler', pct: '10%', color: 'text-amber-600' },
                  { label: 'Otros gastos', pct: '20%', color: 'text-purple-600' },
                  { label: 'Beneficio neto', pct: '7%', color: 'text-emerald-600' },
                ].map(({ label, pct, color }) => (
                  <div key={label} className="text-center p-3 bg-white rounded-lg border border-slate-200">
                    <p className={`text-2xl font-bold ${color}`}>{pct}</p>
                    <p className="text-xs text-slate-500 mt-1">{label}</p>
                  </div>
                ))}
              </div>
              <p className="text-sm text-slate-500 mt-3 text-center italic">
                Fuente: elaboración propia a partir de datos de Hostelería de España y FEHR (2025)
              </p>
            </div>

            {/* ═══════════ SECTION 3: Tabla de márgenes por tipo ═══════════ */}
            <h2 id="margenes-tipo" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Márgenes de beneficio por tipo de establecimiento
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              No todos los restaurantes son iguales. El tipo de establecimiento determina en gran medida el margen que puedes esperar. Aquí tienes una tabla comparativa con datos del sector hostelero español:
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Tipo de establecimiento</th>
                    <th className="p-3 text-right font-medium">Margen bruto</th>
                    <th className="p-3 text-right font-medium">Margen neto</th>
                    <th className="p-3 text-right font-medium">Ticket medio</th>
                    <th className="p-3 text-right font-medium">Food cost</th>
                  </tr>
                </thead>
                <tbody>
                  {marginTable.map(({ tipo, margenBruto, margenNeto, ticketMedio, foodCost }, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{tipo}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{margenBruto}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-semibold text-blue-600">{margenNeto}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{ticketMedio}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-amber-600">{foodCost}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-10">
              Los modelos con menor estructura fija (food trucks, dark kitchens) tienden a tener márgenes netos más altos porque eliminan o reducen drásticamente los costes de alquiler y personal de sala. Sin embargo, su facturación absoluta suele ser menor. Un restaurante casual con un 7% de margen sobre 600.000 €/año genera más beneficio neto (42.000 €) que un food truck con un 15% sobre 200.000 €/año (30.000 €).
            </p>

            {/* ═══════════ SECTION 4: Cómo calcular la rentabilidad ═══════════ */}
            <h2 id="como-calcular" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Cómo calcular la rentabilidad de tu restaurante
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La fórmula fundamental es sencilla, pero aplicarla correctamente requiere tener datos fiables de cada partida:
            </p>

            <div className="bg-blue-50 border border-blue-100 rounded-xl p-6 mb-6">
              <p className="text-blue-800 font-semibold text-center mb-2">Fórmula de beneficio neto</p>
              <p className="text-center text-xl font-bold text-blue-600 font-mono">
                Beneficio neto = Ingresos totales - Costes fijos - Costes variables
              </p>
              <p className="text-center text-slate-600 mt-3 text-sm">
                Margen neto (%) = (Beneficio neto / Ingresos totales) x 100
              </p>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              Vamos a desglosar cada componente con un ejemplo real:
            </p>

            <div className="space-y-3 mb-6">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-medium text-slate-800">Ingresos totales</span>
                  <span className="text-slate-500 ml-2">— Facturación mensual (ventas de comida + bebida + extras)</span>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-medium text-slate-800">Costes fijos</span>
                  <span className="text-slate-500 ml-2">— Alquiler, seguros, amortizaciones, licencias, asesoría (no varían con las ventas)</span>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-medium text-slate-800">Costes variables</span>
                  <span className="text-slate-500 ml-2">— Materia prima, personal eventual, suministros, comisiones de delivery</span>
                </div>
              </div>
            </div>

            <div className="border-2 border-blue-200 rounded-2xl overflow-hidden mb-6">
              <div className="bg-blue-500 text-white p-5">
                <h3 className="text-xl font-bold">Ejemplo: Restaurante "La Esquina" — 60 cubiertos/día</h3>
                <p className="text-blue-100 text-sm mt-1">Cálculo mensual de rentabilidad</p>
              </div>
              <div className="p-5">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <tbody>
                      {[
                        ['Ingresos mensuales', '60 cubiertos x 20 € x 26 días', '31.200 €', 'text-slate-800'],
                        ['(-) Materia prima (30%)', 'Food cost', '-9.360 €', 'text-red-600'],
                        ['(-) Personal (33%)', 'Nóminas + SS', '-10.296 €', 'text-red-600'],
                        ['(-) Alquiler', 'Local 120 m² zona media', '-2.800 €', 'text-red-600'],
                        ['(-) Suministros', 'Luz, agua, gas', '-1.560 €', 'text-red-600'],
                        ['(-) Otros gastos', 'Marketing, software, asesoría', '-1.870 €', 'text-red-600'],
                      ].map(([concepto, detalle, importe, color], i) => (
                        <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                          <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{concepto}</td>
                          <td className="p-3 border-b border-slate-100 text-slate-500 text-sm">{detalle}</td>
                          <td className={`p-3 border-b border-slate-100 text-right font-semibold ${color}`}>{importe}</td>
                        </tr>
                      ))}
                      <tr className="bg-green-50">
                        <td className="p-3 font-bold text-slate-800 text-base" colSpan={2}>Beneficio neto mensual</td>
                        <td className="p-3 text-right font-bold text-green-600 text-lg">5.314 €</td>
                      </tr>
                      <tr className="bg-green-50">
                        <td className="p-3 font-bold text-slate-800" colSpan={2}>Margen neto</td>
                        <td className="p-3 text-right font-bold text-green-600 text-lg">17,0%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-10">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-800">Cuidado con la facturación real vs estimada</p>
                  <p className="text-amber-700 text-sm mt-1">
                    El ejemplo anterior asume 60 cubiertos/día todos los días de apertura. En la realidad, la ocupación media de un restaurante español es del <strong>55-65%</strong>. Calcula siempre con ocupación realista. Si tu local tiene 40 plazas y haces 2 turnos, tu capacidad teórica es 80, pero tu ocupación real serán 44-52 cubiertos/día.
                  </p>
                </div>
              </div>
            </div>

            {/* ═══════════ SECTION 5: Food cost ═══════════ */}
            <h2 id="food-cost" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              El food cost: el coste que más puedes controlar
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              De los cuatro costes principales, el <strong>food cost</strong> es el que tiene mayor impacto inmediato en la rentabilidad y el que puedes optimizar más rápidamente. Reducir el food cost un solo punto porcentual puede representar miles de euros al año:
            </p>

            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              {[
                { facturacion: '300.000 €/año', ahorro: '3.000 €', desc: '1% de reducción' },
                { facturacion: '500.000 €/año', ahorro: '5.000 €', desc: '1% de reducción' },
                { facturacion: '1.000.000 €/año', ahorro: '10.000 €', desc: '1% de reducción' },
              ].map(({ facturacion, ahorro, desc }) => (
                <div key={facturacion} className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                  <p className="text-xs text-slate-500 mb-1">Facturación {facturacion}</p>
                  <p className="text-2xl font-bold text-green-600">{ahorro}</p>
                  <p className="text-xs text-green-700 mt-1">{desc}</p>
                </div>
              ))}
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              El food cost se calcula como el porcentaje que representan los ingredientes sobre el precio de venta del plato. La forma más precisa de controlarlo es con <strong>escandallos</strong>: fichas de coste que desglosan cada ingrediente, su cantidad, merma y precio de compra.
            </p>

            <div className="space-y-3 mb-6">
              {[
                'Cada plato de tu carta debe tener un escandallo actualizado',
                'Los precios de compra deben revisarse cada vez que recibes una factura o albarán',
                'Las mermas (lo que se pierde al limpiar, pelar o cocinar) encarecen el coste real un 15-50%',
                'El food cost objetivo varía por categoría: entrantes 25-28%, principales 28-33%, postres 20-25%',
              ].map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-10">
              Para una guía completa sobre food cost, consulta nuestro artículo <Link to="/food-cost" className="text-blue-600 hover:underline">Food Cost: qué es y cómo calcularlo</Link>. Si quieres aprender a hacer escandallos, te recomendamos nuestra guía <Link to="/escandallo" className="text-blue-600 hover:underline">Qué es un escandallo</Link> o el tutorial <Link to="/como-hacer-escandallo" className="text-blue-600 hover:underline">Cómo hacer un escandallo paso a paso</Link>.
            </p>

            {/* ═══════════ SECTION 6: 7 estrategias ═══════════ */}
            <h2 id="estrategias" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              7 estrategias para mejorar la rentabilidad de tu restaurante
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Mejorar la rentabilidad no significa recortar calidad. Estas siete estrategias están probadas en miles de restaurantes españoles y se pueden implementar sin grandes inversiones:
            </p>

            <div className="space-y-6 mb-10">
              <div className="border-l-4 border-blue-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2 flex items-center gap-2">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-500 text-white font-bold flex items-center justify-center text-sm">1</span>
                  Ingeniería de menú
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  Clasifica tus platos en cuatro categorías según rentabilidad y popularidad: <strong>Stars</strong> (rentables y populares: promociónalos), <strong>Plowhorses</strong> (populares pero poco rentables: sube precio o reduce coste), <strong>Puzzles</strong> (rentables pero poco vendidos: mejora su visibilidad) y <strong>Dogs</strong> (ni rentables ni populares: elimínalos o reformúlalos). Un análisis de ingeniería de menú bien hecho puede mejorar el margen global un 3-5%.
                </p>
                <Link to="/ingenieria-de-menu" className="text-sm text-blue-600 hover:underline mt-2 block">Guía completa de ingeniería de menú &rarr;</Link>
              </div>

              <div className="border-l-4 border-blue-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2 flex items-center gap-2">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-500 text-white font-bold flex items-center justify-center text-sm">2</span>
                  Negociar con proveedores
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  Compara precios entre proveedores de forma sistemática. Pide tres presupuestos para cada categoría de producto. Negocia descuentos por volumen, pronto pago o exclusividad. Los precios de mercado mayorista (Mercabarna, Mercamadrid) son tu referencia para saber si estás pagando un precio justo. Una buena negociación puede reducir el food cost entre un 2% y un 5%.
                </p>
              </div>

              <div className="border-l-4 border-blue-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2 flex items-center gap-2">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-500 text-white font-bold flex items-center justify-center text-sm">3</span>
                  Reducir mermas y desperdicios
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  Las mermas representan entre un 5% y un 15% del coste de materia prima. Controla el peso antes y después de la manipulación, forma a tu equipo en técnicas de corte eficientes, aprovecha subproductos (caldos con recortes, croquetas con sobrantes) y monitoriza las fechas de caducidad. Un programa de reducción de desperdicios puede ahorrar entre 500 € y 2.000 €/mes.
                </p>
                <Link to="/mermas-cocina" className="text-sm text-blue-600 hover:underline mt-2 block">Guía de mermas en cocina &rarr;</Link>
              </div>

              <div className="border-l-4 border-blue-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2 flex items-center gap-2">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-500 text-white font-bold flex items-center justify-center text-sm">4</span>
                  Optimizar la carta
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  Una carta con demasiados platos (más de 40 referencias) genera exceso de stock, más merma y mayor complejidad operativa. Reduce la carta a 25-35 platos bien escandallados, usa ingredientes compartidos entre recetas para aprovechar volumen de compra, y rota la carta estacionalmente para usar productos de temporada (más baratos y de mejor calidad).
                </p>
              </div>

              <div className="border-l-4 border-blue-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2 flex items-center gap-2">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-500 text-white font-bold flex items-center justify-center text-sm">5</span>
                  Controlar desperdicios con datos
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  Lo que no se mide no se puede mejorar. Registra diariamente: platos devueltos, género caducado, sobrantes de preparación y desperdicios de servicio. La Ley 7/2022 de residuos ya obliga a los restaurantes a registrar el desperdicio alimentario. Conviértelo en una ventaja competitiva usándolo para tomar decisiones de compra y producción.
                </p>
              </div>

              <div className="border-l-4 border-blue-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2 flex items-center gap-2">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-500 text-white font-bold flex items-center justify-center text-sm">6</span>
                  Usar tecnología de control de costes
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  Un software de escandallos automatiza la actualización de precios (OCR de albaranes), calcula el food cost en tiempo real, detecta desviaciones y genera alertas cuando un plato supera el umbral. La inversión (50-150 €/mes) se recupera el primer mes con la reducción de food cost que genera. Sin tecnología, es imposible gestionar los costes de un restaurante con más de 30 recetas.
                </p>
              </div>

              <div className="border-l-4 border-blue-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2 flex items-center gap-2">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-500 text-white font-bold flex items-center justify-center text-sm">7</span>
                  Revisar precios de venta periódicamente
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  El error más común es fijar precios al abrir y no revisarlos en años. Los costes suben continuamente (materias primas, energía, salario mínimo), pero muchos hosteleros temen subir precios por miedo a perder clientes. La realidad es que una subida del 5% en la carta apenas se percibe, pero puede suponer un 20-30% más de beneficio neto. Revisa precios al menos cada trimestre usando los datos de tus escandallos actualizados.
                </p>
                <Link to="/calcular-precio-plato" className="text-sm text-blue-600 hover:underline mt-2 block">Cómo calcular el precio de venta de un plato &rarr;</Link>
              </div>
            </div>

            {/* ═══════════ SECTION 7: Semáforo de rentabilidad ═══════════ */}
            <h2 id="semaforo" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              El semáforo de rentabilidad: ¿dónde está tu restaurante?
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Usa esta tabla para evaluar rápidamente la salud financiera de tu restaurante según tu margen neto:
            </p>

            <div className="space-y-3 mb-10">
              {trafficLight.map(({ rango, nivel, color, dot, desc }) => (
                <div key={nivel} className={`flex gap-4 p-4 rounded-xl border ${color}`}>
                  <div className="flex-shrink-0 flex items-center">
                    <span className={`w-4 h-4 rounded-full ${dot}`} />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold">
                      {nivel} <span className="font-normal opacity-75">— Margen neto {rango}</span>
                    </p>
                    <p className="text-sm mt-1 opacity-90">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* ═══════════ SECTION 8: Caso práctico ═══════════ */}
            <h2 id="caso-practico" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Caso práctico: restaurante que pasó de 2% a 9% de margen
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Este es un caso real (datos anonimizados) de un restaurante casual de 45 plazas en una ciudad mediana de Catalunya que utilizó PLEKO para transformar su rentabilidad en 4 meses:
            </p>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 mb-6">
              <h3 className="font-bold text-slate-800 mb-4">Situación inicial (enero 2026)</h3>
              <div className="grid sm:grid-cols-4 gap-3 mb-6">
                {[
                  { label: 'Facturación', valor: '38.000 €/mes' },
                  { label: 'Food cost', valor: '36%' },
                  { label: 'Margen neto', valor: '2,1%' },
                  { label: 'Beneficio', valor: '798 €/mes' },
                ].map(({ label, valor }) => (
                  <div key={label} className="text-center p-3 bg-white rounded-lg border border-red-200">
                    <p className="text-xs text-slate-500">{label}</p>
                    <p className="text-lg font-bold text-red-600">{valor}</p>
                  </div>
                ))}
              </div>

              <h3 className="font-bold text-slate-800 mb-3">Acciones realizadas (febrero-mayo 2026)</h3>
              <div className="space-y-3 mb-6">
                {[
                  { mes: 'Febrero', accion: 'Escandallaron los 42 platos de la carta con PLEKO. Descubrieron que 8 platos tenían un food cost superior al 40% sin que lo supieran.' },
                  { mes: 'Marzo', accion: 'Reformularon 5 platos (sustituyeron ingredientes caros por alternativas de calidad similar), eliminaron 3 "Dogs" y subieron el PVP un 8% en 12 platos que estaban por debajo del mercado.' },
                  { mes: 'Abril', accion: 'Implementaron control de mermas con pesaje diario. Redujeron el desperdicio un 22%. Negociaron con 2 proveedores nuevos para pescado y verdura, ahorrando un 6% medio.' },
                  { mes: 'Mayo', accion: 'Activaron el OCR de albaranes: todos los precios se actualizan automáticamente. Crearon un menú del día escandallado con margen garantizado del 68%.' },
                ].map(({ mes, accion }) => (
                  <div key={mes} className="flex gap-3 p-3 bg-white rounded-lg border border-slate-200">
                    <span className="font-bold text-blue-600 flex-shrink-0 w-20">{mes}</span>
                    <p className="text-slate-700 text-sm">{accion}</p>
                  </div>
                ))}
              </div>

              <h3 className="font-bold text-slate-800 mb-3">Resultado (mayo 2026)</h3>
              <div className="grid sm:grid-cols-4 gap-3">
                {[
                  { label: 'Facturación', valor: '41.200 €/mes', cambio: '+8,4%' },
                  { label: 'Food cost', valor: '28,5%', cambio: '-7,5 pp' },
                  { label: 'Margen neto', valor: '9,2%', cambio: '+7,1 pp' },
                  { label: 'Beneficio', valor: '3.790 €/mes', cambio: '+375%' },
                ].map(({ label, valor, cambio }) => (
                  <div key={label} className="text-center p-3 bg-white rounded-lg border border-green-200">
                    <p className="text-xs text-slate-500">{label}</p>
                    <p className="text-lg font-bold text-green-600">{valor}</p>
                    <p className="text-xs text-green-500 mt-1">{cambio}</p>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-10">
              La transformación no requirió grandes inversiones ni cambios drásticos en la cocina. Se basó en <strong>tres pilares: datos fiables (escandallos), decisiones informadas (ingeniería de menú) y automatización (OCR + alertas de food cost)</strong>. El coste del software fue de 79 €/mes; el retorno, más de 2.990 €/mes en beneficio adicional.
            </p>

            {/* ═══════════ SECTION 9: Herramientas ═══════════ */}
            <h2 id="herramientas" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Herramientas para controlar la rentabilidad
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Controlar la rentabilidad requiere herramientas que te den visibilidad en tiempo real sobre tus costes. Estas son las más utilizadas en hostelería:
            </p>

            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <div className="border border-slate-200 rounded-xl p-5">
                <h3 className="font-bold text-slate-700 mb-3 flex items-center gap-2">
                  <Target className="w-5 h-5 text-slate-500" />
                  Escandallos (fichas de coste)
                </h3>
                <p className="text-sm text-slate-600 mb-3">
                  El escandallo es la base de todo control de costes. Sin saber cuánto te cuesta cada plato, es imposible tomar decisiones informadas sobre precios, carta o proveedores.
                </p>
                <Link to="/escandallo" className="text-sm text-blue-600 hover:underline">Guía de escandallos &rarr;</Link>
              </div>

              <div className="border border-slate-200 rounded-xl p-5">
                <h3 className="font-bold text-slate-700 mb-3 flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-slate-500" />
                  Ingeniería de menú
                </h3>
                <p className="text-sm text-slate-600 mb-3">
                  Combina datos de rentabilidad (escandallo) y popularidad (ventas) para clasificar platos y optimizar la carta. La herramienta más potente para mejorar el margen global.
                </p>
                <Link to="/ingenieria-de-menu" className="text-sm text-blue-600 hover:underline">Guía de ingeniería de menú &rarr;</Link>
              </div>

              <div className="border-2 border-blue-300 bg-blue-50 rounded-xl p-5">
                <h3 className="font-bold text-blue-700 mb-3 flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-blue-500" />
                  Software de control de costes
                </h3>
                <p className="text-sm text-slate-700 mb-3">
                  PLEKO automatiza escandallos, OCR de albaranes, control de food cost, ingeniería de menú, precios de mercado en tiempo real y alertas de rentabilidad. Todo integrado en una plataforma.
                </p>
                <Link to="/software-escandallo" className="text-sm text-blue-700 font-semibold hover:underline">Ver PLEKO &rarr;</Link>
              </div>
            </div>

            {/* ═══════════ SECTION 10: Errores ═══════════ */}
            <h2 id="errores" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              10 errores que destruyen la rentabilidad de un restaurante
            </h2>

            <div className="space-y-4 mb-10">
              {[
                { error: 'No hacer escandallos', desc: 'Sin escandallo, no sabes cuánto te cuesta cada plato. Estás fijando precios a ciegas.' },
                { error: 'No actualizar precios de compra', desc: 'Los precios cambian cada semana. Un escandallo con precios de hace 3 meses miente sobre tu margen real.' },
                { error: 'Ignorar las mermas', desc: 'Un kilo de gambas a 22 € con 40% de merma realmente cuesta 36,67 €/kg utilizable.' },
                { error: 'No revisar los precios de venta', desc: 'Los costes suben pero los precios de la carta no. El margen se erosiona silenciosamente.' },
                { error: 'Carta demasiado extensa', desc: 'Más platos = más stock, más merma, más complejidad operativa y menor rotación de producto.' },
                { error: 'No medir el food cost real', desc: 'El food cost teórico (escandallo) y el real (compras vs ventas) pueden diferir un 5-8% por desperdicio.' },
                { error: 'Copiar precios de la competencia', desc: 'Cada restaurante tiene costes diferentes. Tu precio debe basarse en TU escandallo, no en el del vecino.' },
                { error: 'No negociar con proveedores', desc: 'Aceptar el precio del primer proveedor sin comparar puede costarte un 5-15% de sobreprecio.' },
                { error: 'Sobredimensionar la plantilla', desc: 'El personal es el coste más alto. Cada puesto innecesario reduce el margen 1-2 puntos.' },
                { error: 'No separar las cuentas personales de las del negocio', desc: 'Mezclar finanzas personales y empresariales hace imposible conocer la rentabilidad real.' },
              ].map(({ error, desc }, i) => (
                <div key={i} className="flex gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-slate-800">{i + 1}. {error}</p>
                    <p className="text-slate-600 text-sm mt-1">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ═══════════ FAQ ═══════════ */}
          <div id="faqs" className="mt-16 scroll-mt-20">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">
              Preguntas frecuentes sobre la rentabilidad de restaurantes
            </h2>
            <div className="space-y-4">
              {faqs.map(({ q, a }) => (
                <details key={q} className="border border-slate-200 rounded-xl overflow-hidden">
                  <summary className="p-5 font-semibold text-slate-800 cursor-pointer hover:bg-slate-50 flex justify-between items-center list-none">
                    {q}
                    <span className="text-slate-400 text-xl ml-4">+</span>
                  </summary>
                  <div className="px-5 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">{a}</div>
                </details>
              ))}
            </div>
          </div>

          {/* ═══════════ INTERNAL LINKS ═══════════ */}
          <div className="mt-12 p-6 bg-slate-50 rounded-xl border border-slate-200">
            <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-blue-500" /> Guías relacionadas
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { to: '/escandallo', label: 'Qué es un escandallo: guía completa' },
                { to: '/food-cost', label: 'Food Cost: qué es y cómo calcularlo' },
                { to: '/margen-beneficio-restaurante', label: 'Margen de beneficio en hostelería' },
                { to: '/calcular-precio-plato', label: 'Cómo calcular el precio de un plato' },
                { to: '/mermas-cocina', label: 'Mermas en cocina: tabla completa' },
                { to: '/ingenieria-de-menu', label: 'Ingeniería de menú: guía práctica' },
                { to: '/control-costes-restaurante', label: 'Control de costes en restaurantes' },
                { to: '/software-escandallo', label: 'El mejor software de escandallos' },
              ].map(({ to, label }) => (
                <Link key={to} to={to} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline">
                  <ArrowRight className="w-3 h-3" /> {label}
                </Link>
              ))}
            </div>
          </div>

          {/* ═══════════ FINAL CTA ═══════════ */}
          <div className="mt-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-8 text-center text-white">
            <TrendingUp className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl font-bold mb-3">
              Controla la rentabilidad de tu restaurante con datos reales
            </h2>
            <p className="text-blue-100 mb-6 max-w-xl mx-auto">
              PLEKO calcula automáticamente el coste de todas tus recetas, controla el food cost en tiempo real, escanea albaranes con IA y te avisa cuando los márgenes se reducen. Prueba gratis 14 días.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-blue-600 font-bold rounded-xl hover:bg-blue-50 transition-colors"
            >
              Empezar gratis
              <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="mt-3 text-blue-200 text-sm">Sin tarjeta de crédito. Configurado en 2 minutos.</p>
          </div>
        </section>
      </div>
    </>
  );
}
